import { apiClient } from "@/lib/api";
import type { Changed } from "@/components/table/changedColumns";

// Commercie → Diesel (docs/tms-facturatie.md §14): the price of diesel from the day it changed, the named
// methods that make a month's average into the surcharge percentage, and per unloading month what each gives.

export type DieselKind = "steps" | "linear" | "manual";
export type DieselRounding = "down" | "tenth" | "up";

export interface DieselMonthPercent {
  percent: number;
  /** The month whose average gave it; null for a percentage passed on by hand. */
  month: string | null;
  /** The last known one, while the month it asks for is not over yet. */
  provisional: boolean;
}

export interface DieselMonth {
  /** The unloading month, YYYY-MM. */
  month: string;
  /** The month whose average the unloading month takes (the one before). */
  averagedMonth: string;
  /** € per litre; null while that month is not over. */
  average: number | null;
  percents: Record<string, DieselMonthPercent | null>;
}

export interface DieselPrice extends Changed {
  id: string;
  validFrom: string;
  /** Cents per litre. */
  cents: number;
  note: string | null;
}

export interface DieselManualRate {
  id: string;
  validFrom: string;
  validTo: string | null;
  percent: number;
}

export interface DieselMethod extends Changed {
  id: string;
  name: string;
  kind: DieselKind;
  basePrice: number | null;
  step: number | null;
  rounding: DieselRounding | null;
  perEuro: number | null;
  offset: number | null;
  minimum: number | null;
  sameMonth: boolean;
  groupNumber: number | null;
  isActive: boolean;
  customers: number;
  manualRates: DieselManualRate[];
}

export interface DieselOverview {
  today: string;
  months: DieselMonth[];
  prices: DieselPrice[];
  priceCount: number;
  methods: DieselMethod[];
}

export interface DieselMethodInput {
  name?: string;
  kind?: DieselKind;
  basePrice?: number | null;
  step?: number | null;
  rounding?: DieselRounding | null;
  perEuro?: number | null;
  offset?: number | null;
  minimum?: number | null;
  sameMonth?: boolean;
  isActive?: boolean;
}

export const dieselKeys = {
  all: ["diesel"] as const,
  overview: () => [...dieselKeys.all, "overview"] as const,
  options: () => [...dieselKeys.all, "options"] as const,
};

export function getDiesel(): Promise<DieselOverview> {
  return apiClient.get<DieselOverview>("/api/v1/billing/diesel");
}

export function dieselMethodOptions(): Promise<{ id: string; name: string; kind: DieselKind }[]> {
  return apiClient.get("/api/v1/billing/diesel/method-options");
}

export function saveDieselPrice(input: { validFrom: string; cents: number; note: string | null }): Promise<unknown> {
  return apiClient.post("/api/v1/billing/diesel/prices", input);
}

export function deleteDieselPrice(id: string): Promise<unknown> {
  return apiClient.delete(`/api/v1/billing/diesel/prices/${id}`);
}

export function createDieselMethod(input: DieselMethodInput): Promise<{ id: string }> {
  return apiClient.post("/api/v1/billing/diesel/methods", input);
}

export function updateDieselMethod(id: string, input: DieselMethodInput): Promise<unknown> {
  return apiClient.patch(`/api/v1/billing/diesel/methods/${id}`, input);
}

export function addDieselRate(methodId: string, input: { validFrom: string; validTo: string | null; percent: number }): Promise<unknown> {
  return apiClient.post(`/api/v1/billing/diesel/methods/${methodId}/rates`, input);
}

export function deleteDieselRate(methodId: string, rateId: string): Promise<unknown> {
  return apiClient.delete(`/api/v1/billing/diesel/methods/${methodId}/rates/${rateId}`);
}
